import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { BaseProfile, ProfileSelectorConfig } from '@interfaces/profile';
import { TranslateService } from '@ngx-translate/core';
import { LogService } from '@services/log.service';
import { FormGroup, UntypedFormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { ConfirmationDialogService } from 'src/app/modules/confirmation-dialog/confirmation-dialog.service';
import { ProfileSelectorComponent } from '../profile-selector/profile-selector.component';

export enum PanelStates {
  NoFilters = 'noFilters',
  Filters = 'filters',
  Edit = 'edit',
  NoSelectedProfile = 'noSelectedProfile',
}

@Component({
  selector: 'app-profile-settings-tab-display',
  templateUrl: './profile-settings-tab-display.component.html',
  styleUrls: ['./profile-settings-tab-display.component.scss']
})
export class ProfileSettingsTabDisplayComponent implements OnChanges {
  @ViewChild('filterProfileSelector') profileSelector: ProfileSelectorComponent;
  @Input() profiles: BaseProfile[];
  @Input() config: ProfileSelectorConfig;
  @Input() displayFooter: boolean = true; 
  @Input() displayPreview: boolean = false;
  @Input() displaySave: boolean = true;
  @Input() displayCancel: boolean = true;
  @Input() displayReset: boolean = true;
  @Input() displayDelete: boolean = true;
  @Input() displayCopy: boolean = true;
  @Input() displayView: boolean = true;
  @Input() displayApply: boolean = true;

  @Input() selectedProfile: BaseProfile;
  @Input() appliedProfile: BaseProfile;
  @Input() loading: boolean = false;
  @Input() disableSave = false;
  @Input() disableCancel = false;
  @Input() disableReset = false;
  @Input() formGroup: FormGroup | UntypedFormGroup;

  @Output() editProfileClicked = new EventEmitter<BaseProfile>();
  @Output() createProfileClicked = new EventEmitter<void>();
  @Output() applyProfileClicked = new EventEmitter<BaseProfile>();
  @Output() copyProfileClicked = new EventEmitter<BaseProfile>();
  @Output() deleteProfileClicked = new EventEmitter<BaseProfile>();
  @Output() saveProfileClicked = new EventEmitter<BaseProfile>();
  @Output() cancelProfileClicked = new EventEmitter<void>();
  @Output() resetProfileClicked = new EventEmitter<void>();
  @Output() previewProfileClicked = new EventEmitter<void>();

  readonly PanelStates = PanelStates;
  settingsContentPanelState: PanelStates;

  /** The profile being edited is the one on the grid - shown as a badge instead of an Apply button. */
  get isApplied(): boolean {
    const idField = this.config?.profileIdField;
    return !!idField && !!this.selectedProfile && this.selectedProfile[idField] === this.appliedProfile?.[idField];
  }
  destroy$ = new Subject<void>();
  constructor(
    protected readonly logger: LogService,
    protected readonly translate: TranslateService,
    protected readonly changeDetectorRef: ChangeDetectorRef,
    protected readonly confirmService: ConfirmationDialogService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Don't process changes if config is not set yet
    if (!this.config) {
      return;
    }

    if (!this.profiles || this.profiles.length === 0) {
      this.settingsContentPanelState = PanelStates.NoFilters;
      return;
    }

    if(!this.selectedProfile) {
      this.settingsContentPanelState = PanelStates.NoSelectedProfile;
      return;
    }
      this.settingsContentPanelState = PanelStates.Edit;
  }

  onCancel(): void {
    if(this.formGroup.dirty) {
      this.confirmService.showModelIsDirtyDialog().subscribe(result => {
        if(result) {
          this.cancelProfileClicked.emit();
        }
      });
    } else {
      this.cancelProfileClicked.emit();
    }
  }

  onReset(): void {
    this.resetProfileClicked.emit();
  }
  onPreview(): void {
    this.previewProfileClicked.emit();
  }
  //Apply is disabled while the form is dirty, so this never applies a stale copy of an edited profile
  onApply(): void {
    if (this.formGroup?.dirty || this.isApplied) return;
    this.applyProfileClicked.emit(this.selectedProfile);
  }

  onSave(): void {
    this.saveProfileClicked.emit(this.selectedProfile);
  }

  public editProfile(profile: BaseProfile): void {
    this.profileSelector.editProfile(profile);
  }
}
